import React from 'react';
import clsx from 'clsx';

import Icon from '../Icon';
import { withAccess } from '../withAccess';
import OverlayTooltip from '../OverlayTooltip';

export type ButtonStyle = 'primary' | 'light' | 'danger';
type ButtonWidth = 'small' | 'medium' | 'big' | 'full';

export type ButtonProps = AccessProps & {
  icon?: IconType;
  tooltip?: string;

  loading?: boolean;
  disabled?: boolean;
  type?: ButtonStyle;
  label?: React.ReactNode;
  children?: React.ReactNode;

  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;

  round?: boolean;
  width?: ButtonWidth;
  className?: string;
  textSizeClassName?: string;
};

const styleMap: { [x in ButtonStyle]: string } = {
  danger: 'bg-[#ef4444] text-white border-[#ef4444]',
  light: 'bg-content text-text border-active',
  primary: 'bg-active text-text border-active',
};

const nonDisabledStyles: { [x in ButtonStyle]?: string } = {
  light: 'hover:bg-active',
  danger: 'opacity-80 hover:opacity-100',
};

const widthMap: { [x in ButtonWidth]: string } = {
  small: 'px-lg',
  medium: 'px-2xl',
  big: 'px-3xl',
  full: 'w-full',
};

const roundWidthMap: { [x in ButtonWidth]: string } = {
  small: 'size-xl',
  medium: 'size-2xl',
  big: 'size-4xl',
  full: '',
};

const ButtonComponent: React.FC<ButtonProps> = ({
  icon,
  type = 'light',
  width = 'medium',

  tooltip,
  label,
  children,
  loading,
  className,
  round = false,
  disabled: initialDisabled,
  textSizeClassName = 'text-small',

  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const disabled = initialDisabled || loading;
  const content = children || label;

  const element = (
    <button
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={!disabled ? onClick : undefined}
      className={clsx(
        className,
        textSizeClassName,
        styleMap[type],
        !round ? (content ? widthMap[width] : 'px-md') : roundWidthMap[width],
        disabled ? 'disabled opacity-40' : '',
        !disabled ? nonDisabledStyles[type] : '',
        'inline-flex items-center justify-center gap-x-xs',
        !round ? 'py-sm rounded-sm' : 'rounded-full',
        'font-medium shadow-md border',
        'transition-default'
      )}
    >
      {Boolean(icon || loading) && (
        <Icon
          width
          icon={loading ? 'loading' : icon}
          className={content ? 'text-left' : 'text-center'}
        />
      )}
      {content}
    </button>
  );

  return tooltip ? (
    <OverlayTooltip tooltip={tooltip}>{element}</OverlayTooltip>
  ) : (
    element
  );
};

export default withAccess(ButtonComponent);
